// client/src/utils/cacheFreshness.js
// Reports when a given endpoint's cached copy was last fetched.
//
// Finance data is cached so it can be printed offline, which means a statement or
// receipt produced without a connection may not reflect the live balance. Anything
// financial rendered from cache must therefore say what it is: the service worker
// stamps every stored response with `X-Cached-At`, and this reads it back so the
// view — and the printout — can carry "Data as of <time>".

// MUST stay in sync with VOLATILE_PARAMS in public/service-worker.js.
const VOLATILE_PARAMS = ['_t', '_', 'ts', 'cacheBust', 'cachebust', 'nocache'];

function readStorage(key) {
  try {
    return localStorage.getItem(key);
  } catch (_) {
    return null;
  }
}

// Must mirror the service worker's cacheKeyFor, or we'd look up a key that was
// never written.
function buildCacheKey(path) {
  const configured = (
    process.env.REACT_APP_API_BASE_URL ||
    process.env.VITE_API_BASE_URL ||
    process.env.NEXT_PUBLIC_API_BASE_URL ||
    ''
  ).trim();

  const base = configured || window.location.origin;
  const url = new URL(path, base.endsWith('/') ? base : `${base}/`);

  VOLATILE_PARAMS.forEach((p) => url.searchParams.delete(p));

  const schoolId = readStorage('activeSchoolId') || readStorage('schoolId') || readStorage('scopeSchoolId');
  const ayId = readStorage('currentAcademicYearId') || readStorage('academicYearId');
  const phaseId = readStorage('schoolAdmin_activePhaseId') || readStorage('activePhaseId');
  const phase = readStorage('schoolAdmin_activePhase') || readStorage('activePhase');

  if (schoolId) url.searchParams.set('__sid', schoolId);
  if (ayId) url.searchParams.set('__ay', ayId);
  if (phaseId || phase) url.searchParams.set('__ph', phaseId || phase);
  url.searchParams.sort();

  return url.href;
}

/**
 * When this endpoint's cached response was stored.
 *
 * @param {string} path API path, exactly as the page requests it
 * @returns {Promise<Date|null>} null if nothing is cached or the browser has no Cache API
 */
function stampOf(response) {
  const stamp = response && (response.headers.get('X-Cached-At') || response.headers.get('date'));
  if (!stamp) return null;
  const when = new Date(stamp);
  return Number.isNaN(when.getTime()) ? null : when;
}

// Fall back to matching on pathname alone. Call sites pass a bare endpoint
// ('/api/finance/reports/comprehensive') while the cached entry carries whatever
// query the page actually sent (date ranges, scope params), so an exact-key lookup
// would usually miss. Returns the freshest entry for that path.
async function newestByPathname(pathname) {
  let newest = null;
  const names = await caches.keys();
  for (const name of names) {
    const cache = await caches.open(name);
    for (const request of await cache.keys()) {
      let candidate;
      try { candidate = new URL(request.url); } catch (_) { continue; }
      if (candidate.pathname !== pathname) continue;
      const when = stampOf(await cache.match(request));
      if (when && (!newest || when > newest)) newest = when;
    }
  }
  return newest;
}

/**
 * Freshest cache timestamp across any endpoint under the given path prefixes.
 *
 * Used where a page draws on several endpoints and naming one would be arbitrary.
 * Priming refreshes the whole working set together, so in practice these timestamps
 * cluster tightly.
 *
 * @param {string[]} prefixes pathname prefixes, e.g. ['/api/finance']
 * @returns {Promise<Date|null>}
 */
export async function getNewestCachedAt(prefixes = []) {
  try {
    if (typeof caches === 'undefined' || !prefixes.length) return null;
    let newest = null;
    const names = await caches.keys();
    for (const name of names) {
      const cache = await caches.open(name);
      for (const request of await cache.keys()) {
        let url;
        try { url = new URL(request.url); } catch (_) { continue; }
        if (!prefixes.some((p) => url.pathname.startsWith(p))) continue;
        const when = stampOf(await cache.match(request));
        if (when && (!newest || when > newest)) newest = when;
      }
    }
    return newest;
  } catch (_) {
    return null;
  }
}

export async function getCachedAt(path) {
  try {
    if (typeof caches === 'undefined') return null;

    const key = buildCacheKey(path);
    const exact = await caches.match(key);
    const exactWhen = stampOf(exact);
    if (exactWhen) return exactWhen;

    return await newestByPathname(new URL(key).pathname);
  } catch (_) {
    return null;
  }
}

/**
 * Whether what the user is looking at came from cache rather than the network.
 * Being offline is the reliable signal — a served-from-cache response is otherwise
 * indistinguishable from a fresh one at the call site.
 */
export function isServingStale() {
  return typeof navigator !== 'undefined' && navigator.onLine === false;
}
